import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType, SignupData } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Computed values for subscription status
  const isSubscribed = user?.isSubscriber || false;
  const isSubscriptionExpired = user?.subscriptionExpiration
  ? new Date(user.subscriptionExpiration).toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10)
  : false;

  useEffect(() => {
    // Check for existing token on mount
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const userRole = localStorage.getItem('userRole');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      // Optionally verify token with backend
      verifyToken(savedToken);
    } else if (savedToken && userRole === 'scout') {
      // Handle scout authentication (scouts save differently - fallback)
      const scoutUser: User = {
        id: 0,
        name: localStorage.getItem('userName') || '',
        email: localStorage.getItem('userEmail') || '',
        type: 'scout',
        isSubscriber: false
      };
      setToken(savedToken);
      setUser(scoutUser);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      await authAPI.verifyToken();
    } catch (error) {
      // Token is invalid, clear it
      logout();
    }
  };

  const login = async (email: string, password: string, userType: 'player' | 'admin' | 'bookie') => {
    setLoading(true);
    try {
      const response = userType === 'player'
        ? await authAPI.loginPlayer(email, password)
        : userType === 'bookie'
        ? await authAPI.loginBookie(email, password)
        : await authAPI.loginAdmin(email, password);

      setToken(response.token);
      setUser(response.user);
      
      // Save to localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData: SignupData, userType: 'player' | 'admin' | 'bookie') => {
    setLoading(true);
    try {
      const response = userType === 'player'
        ? await authAPI.signupPlayer(userData)
        : userType === 'bookie'
        ? await authAPI.signupBookie(userData)
        : await authAPI.signupAdmin(userData);

      setToken(response.token);
      setUser(response.user);
      
      // Save to localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
  };

  // Function to update subscription status (for when payment is processed)
  const updateSubscriptionStatus = (isSubscriber: boolean, expirationDate?: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        isSubscriber,
        subscriptionExpiration: expirationDate
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    signup,
    logout,
    loading,
    isSubscribed,
    isSubscriptionExpired,
    updateSubscriptionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
